import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  EMPLOYMENT_TYPE_LABELS,
  EMPLOYMENT_TYPES,
  MAX_DEPARTMENT_LENGTH,
  type EmploymentType,
} from '@wodi/domain';
import type { StaffRecordView } from '@wodi/shared';
import {
  Button,
  DateField,
  LoadingBlock,
  Notice,
  PageHeader,
  Section,
  Select,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import { saveStaffEmployment, staffKeys, staffRecordQuery } from '../api/staff.ts';
import { useOnline } from '../hooks/use-online.ts';
import { toasts } from '../toasts.ts';
import { councilText, dayText, errorText, LoadFailed, StaffLicenceTag } from './format.tsx';
import { LicenceCopies } from './licence-copies.tsx';
import styles from './staff.module.css';

// One person's staff record (docs/plans/phase2-staff-records.md §6): the licence from their
// practitioner profile, their employment details and their licence copies. Opening it is
// audited on the server as a read of a staff record.

const route = getRouteApi('/_app/staff/$userId');

function LicenceFacts({ record, mayChange }: { record: StaffRecordView; mayChange: boolean }) {
  return (
    <Section title="Licence">
      <dl className={styles.facts}>
        <dt>Cadre</dt>
        <dd>{record.cadre?.display ?? 'Not recorded'}</dd>
        <dt>Council</dt>
        <dd>{councilText(record.licenceBody)}</dd>
        <dt>Licence number</dt>
        <dd>
          {record.licenceNumber === null ? (
            'Not recorded'
          ) : (
            <span className="wodi-id">{record.licenceNumber}</span>
          )}
        </dd>
        <dt>Issued</dt>
        <dd>{dayText(record.licenceIssuedOn)}</dd>
        <dt>Expires</dt>
        <dd>{dayText(record.licenceExpiresOn)}</dd>
        <dt>State</dt>
        <dd>
          <StaffLicenceTag licence={record.licence} />
        </dd>
      </dl>
      {mayChange ? (
        <p className={styles.meta}>
          Change the licence details in{' '}
          <Link to="/settings/practitioners">Settings, Practitioners</Link>.
        </p>
      ) : null}
    </Section>
  );
}

function EmploymentFacts({ record }: { record: StaffRecordView }) {
  const e = record.employment;
  return (
    <dl className={styles.facts}>
      <dt>Department</dt>
      <dd>{e?.department ?? 'Not recorded'}</dd>
      <dt>Employment</dt>
      <dd>{e === null ? 'Not recorded' : EMPLOYMENT_TYPE_LABELS[e.employmentType]}</dd>
      <dt>Started</dt>
      <dd>{dayText(e?.startDate ?? null)}</dd>
    </dl>
  );
}

function EmploymentForm({ record, online }: { record: StaffRecordView; online: boolean }) {
  const queryClient = useQueryClient();
  const [department, setDepartment] = useState(record.employment?.department ?? '');
  const [type, setType] = useState<EmploymentType | null>(
    record.employment?.employmentType ?? null,
  );
  const [startDate, setStartDate] = useState<string | null>(record.employment?.startDate ?? null);
  const [tried, setTried] = useState(false);
  const tooLong = department.trim().length > MAX_DEPARTMENT_LENGTH;
  const save = useMutation({
    mutationFn: () =>
      saveStaffEmployment(record.userId, {
        department: department.trim() === '' ? null : department.trim(),
        employmentType: type ?? 'permanent',
        startDate,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(staffKeys.record(record.userId), saved);
      void queryClient.invalidateQueries({ queryKey: staffKeys.list });
      toasts.show({ title: 'Employment details saved' });
    },
  });
  return (
    <form
      className={styles.form}
      aria-label="Employment details"
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (type === null || tooLong) return;
        save.mutate();
      }}
    >
      <div className={styles.fields}>
        <TextField
          label="Department"
          description="Optional, such as Outpatients."
          value={department}
          onChange={setDepartment}
          {...(tooLong
            ? {
                errorMessage: `Keep the department to ${String(MAX_DEPARTMENT_LENGTH)} characters or fewer.`,
              }
            : {})}
        />
        <Select
          label="Employment type"
          placeholder="Choose one"
          options={EMPLOYMENT_TYPES.map((id) => ({ id, label: EMPLOYMENT_TYPE_LABELS[id] }))}
          value={type}
          onChange={(key) => {
            setType(key === null ? null : (String(key) as EmploymentType));
          }}
          {...(tried && type === null ? { errorMessage: 'Choose how they are employed.' } : {})}
        />
        <DateField label="Start date (optional)" value={startDate} onChange={setStartDate} />
      </div>
      {save.isError ? (
        <Notice tone="error">
          {errorText(save.error, "The details weren't saved. Try again.")}
        </Notice>
      ) : null}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save employment details
        </Button>
        {online ? null : (
          <p className={styles.meta}>Saving works again when you&apos;re back online.</p>
        )}
      </div>
    </form>
  );
}

export function StaffRecordPage() {
  const { userId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const allowed = session !== null && can(session, 'staff.read');
  const record = useQuery({ ...staffRecordQuery(userId), enabled: allowed });

  if (!allowed) {
    return (
      <>
        <PageHeader title="Staff record" />
        <Notice tone="info">
          You don&apos;t have permission to see staff records. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  const mayManage = can(session, 'staff.manage');
  return (
    <div className={styles.page}>
      <PageHeader title={record.data?.displayName ?? 'Staff record'} />
      <p className={styles.meta}>
        <Link to="/staff">All staff</Link>
      </p>
      {record.isPending ? <LoadingBlock label="Loading the staff record" rows={6} /> : null}
      {record.isError ? (
        <LoadFailed
          error={record.error}
          fallback="The staff record couldn't be loaded. Try again."
          onRetry={() => void record.refetch()}
        />
      ) : null}
      {record.isSuccess ? (
        <>
          {record.data.userStatus === 'disabled' ? (
            <Notice tone="info">This account is disabled. The record is kept.</Notice>
          ) : null}
          <LicenceFacts record={record.data} mayChange={can(session, 'practitioners.manage')} />
          <Section title="Employment">
            {mayManage ? (
              <EmploymentForm key={record.data.userId} record={record.data} online={online} />
            ) : (
              <EmploymentFacts record={record.data} />
            )}
          </Section>
          <Section title="Licence copies" description="Newest first.">
            <LicenceCopies
              userId={record.data.userId}
              documents={record.data.documents}
              mayManage={mayManage}
              online={online}
            />
          </Section>
        </>
      ) : null}
    </div>
  );
}
